import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { Badge } from "@/shared/components/ui/Badge";
import { companyService } from "@/features/company/services/companyService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import {
  bulkUserService,
  type ExportFilters,
  type ImportResult,
  type ReferenceCodes,
  type UploadLogEntry,
  type UploadLogRow,
} from "@/features/other-setup/services/bulkUserService";

type ResultTab = "succeeded" | "failed";

interface CompanyOption { value: string; label: string; }

// ── Client-side pre-check ─────────────────────────────────────────────────────

interface PreCheckIssue { row: number; name: string; missing: string[]; invalid: string[]; }
interface PreCheckResult { total: number; ready: number; createCount: number; updateCount: number; issues: PreCheckIssue[]; preview: Record<string, string>[]; headers: string[]; }

// Complete header → field map for ALL 32 columns (EN + JA)
const HEADER_TO_FIELD: Record<string, string> = {
  "User ID (Do Not Change)":   "user_id",                  "ユーザーID（変更不可）": "user_id",
  "Temporary Login ID (New Users Only)": "temp_login_id", "仮ログインID（新規ユーザーのみ）": "temp_login_id",
  "Last Name":                 "last_name",                "姓":                    "last_name",
  "First Name":                "first_name",               "名":                    "first_name",
  "Middle Name":               "middle_name",              "ミドルネーム":           "middle_name",
  "Sending Organization":      "sending_org",              "送出機関":               "sending_org",
  "Company Code":              "company_code",             "企業コード":             "company_code",
  "Batch Number":              "batch_no",                 "バッチ番号":             "batch_no",
  "Position":                  "position",                 "役職":                  "position",
  "Company Joining Date":      "company_joining_date",     "入社日":                "company_joining_date",
  "Visa Type":                 "visa_type",                "ビザ種別":               "visa_type",
  "Visa Number":               "visa_number",              "ビザ番号":               "visa_number",
  "Visa Issue Date":           "visa_issue_date",          "ビザ発行日":             "visa_issue_date",
  "Visa Expiry Date":          "visa_expiry_date",         "ビザ有効期限":           "visa_expiry_date",
  "Passport Number":           "passport_no",              "パスポート番号":          "passport_no",
  "Passport Name":             "passport_name",            "パスポート氏名":          "passport_name",
  "Passport Expiry Date":      "passport_expiry",          "パスポート有効期限":      "passport_expiry",
  "Passport Issuing Country":  "passport_issuing_country", "パスポート発行国":        "passport_issuing_country",
  "Issuing Authority":         "issuing_authority",        "発行機関":               "issuing_authority",
  "Employment Start Date":     "joining_date",             "雇用開始日":             "joining_date",
  "Employment End Date":       "assignment_start_date",    "雇用終了日":             "assignment_start_date",
  "City":                      "city",                     "市区町村":               "city",
  "Country":                   "country",                  "国":                    "country",
  "State / Province":          "state_province",           "都道府県":               "state_province",
  "Street Address":            "street_address",           "番地・建物名":            "street_address",
  "Postal Code":               "postal_code",              "郵便番号":               "postal_code",
  "Date of Birth":             "birthdate",                "生年月日":               "birthdate",
  "Gender":                    "gender",                   "性別":                  "gender",
  "Phone Number":              "phone_number",             "電話番号":               "phone_number",
  "Emergency Contact Name":    "emergency_contact_name",   "緊急連絡先氏名":          "emergency_contact_name",
  "Emergency Contact Number":  "emergency_contact_number", "緊急連絡先電話番号":       "emergency_contact_number",
  "Emergency Email":           "emergency_email",          "緊急連絡先メール":        "emergency_email",
  "Emergency Contact Address": "emergency_contact_address","緊急連絡先住所":          "emergency_contact_address",
};

const DATE_FIELDS = new Set(["company_joining_date","visa_issue_date","visa_expiry_date","passport_expiry","joining_date","assignment_start_date","birthdate"]);
const DATE_REGEX  = /^\d{4}\/\d{1,2}\/\d{1,2}$/;
const VALID_GENDERS_CLIENT  = new Set(["MALE","FEMALE"]);
const VALID_COUNTRIES_CLIENT = new Set(["JP","PH","VN","ID","MM","KH","TH","CN","NP","IN","BD","LK"]);

// ── Field-length limits (mirror Zod validator) ────────────────────────────────
const FIELD_LIMITS: Record<string, { max: number; label: string }> = {
  last_name:                 { max: 100, label: "Last Name" },
  first_name:                { max: 100, label: "First Name" },
  middle_name:               { max: 100, label: "Middle Name" },
  sending_org:               { max: 50,  label: "Sending Organization" },
  batch_no:                  { max: 50,  label: "Batch Number" },
  position:                  { max: 100, label: "Position" },
  visa_number:               { max: 50,  label: "Visa Number" },
  passport_no:               { max: 30,  label: "Passport Number" },
  passport_name:             { max: 150, label: "Passport Name" },
  passport_issuing_country:  { max: 50,  label: "Passport Issuing Country" },
  issuing_authority:         { max: 150, label: "Issuing Authority" },
  city:                      { max: 100, label: "City" },
  state_province:            { max: 100, label: "State / Province" },
  street_address:            { max: 255, label: "Street Address" },
  postal_code:               { max: 20,  label: "Postal Code" },
  phone_number:              { max: 30,  label: "Phone Number" },
  emergency_contact_name:    { max: 150, label: "Emergency Contact Name" },
  emergency_contact_number:  { max: 30,  label: "Emergency Contact Number" },
  emergency_email:           { max: 255, label: "Emergency Email" },
  emergency_contact_address: { max: 255, label: "Emergency Contact Address" },
};
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEMP_LOGIN_ID_REGEX = /^[a-z0-9][a-z0-9._-]{2,49}$/i;

const REQUIRED_FIELDS: Array<{ field: string; label: string }> = [
  { field: "last_name",       label: "Last Name" },
  { field: "sending_org",     label: "Sending Organization" },
  { field: "company_code",    label: "Company Code" },
  { field: "batch_no",        label: "Batch Number" },
  { field: "visa_type",       label: "Visa Type" },
  { field: "visa_number",     label: "Visa Number" },
  { field: "visa_expiry_date",label: "Visa Expiry Date" },
  { field: "passport_no",     label: "Passport Number" },
  { field: "passport_name",   label: "Passport Name" },
  { field: "passport_expiry", label: "Passport Expiry Date" },
  { field: "country",         label: "Country" },
  { field: "birthdate",       label: "Date of Birth" },
  { field: "gender",          label: "Gender" },
];

// Rows that create a worker (blank user_id + temp_login_id) only need what the
// backend's bulkCreateRowSchema requires — visa/passport details can follow later.
const CREATE_REQUIRED_FIELDS: Array<{ field: string; label: string }> = [
  { field: "first_name",   label: "First Name" },
  { field: "company_code", label: "Company Code" },
  { field: "batch_no",     label: "Batch Number" },
  { field: "sending_org",  label: "Sending Organization" },
  { field: "country",      label: "Country" },
];

const isCreateRow = (row: Record<string, string>) => !row.user_id?.trim() && !!row.temp_login_id?.trim();
const requiredFieldsFor = (row: Record<string, string>) => (isCreateRow(row) ? CREATE_REQUIRED_FIELDS : REQUIRED_FIELDS);

function parseClientCsv(text: string): { rows: Record<string, string>[]; rawHeaders: string[] } {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], rawHeaders: [] };

  const parseRow = (line: string): string[] => {
    const cols: string[] = [];
    let cur = "", inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === "," && !inQuote) { cols.push(cur); cur = ""; }
      else cur += ch;
    }
    cols.push(cur);
    return cols;
  };

  const rawHeaders = parseRow(lines[0]).map((h) => h.trim());
  const fieldHeaders = rawHeaders.map((h) => HEADER_TO_FIELD[h] ?? h);
  const rows = lines.slice(1).map((line) => {
    const vals = parseRow(line);
    const obj: Record<string, string> = {};
    fieldHeaders.forEach((h, i) => { obj[h] = (vals[i] ?? "").trim(); });
    return obj;
  });
  return { rows, rawHeaders };
}

const PREVIEW_LIMIT = 50;

function runPreCheck(file: File, refs: ReferenceCodes | null): Promise<PreCheckResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { rows, rawHeaders } = parseClientCsv(e.target?.result as string);
        const issues: PreCheckIssue[] = [];
        const tempIdCounts = new Map<string, number>();
        rows.forEach((row) => {
          const id = row.temp_login_id?.trim().toLowerCase();
          if (id) tempIdCounts.set(id, (tempIdCounts.get(id) ?? 0) + 1);
        });
        rows.forEach((row, i) => {
          const missing = requiredFieldsFor(row)
            .filter((f) => !row[f.field]?.trim())
            .map((f) => f.label);

          const invalid: string[] = [];
          const userId = row.user_id?.trim();
          const tempLoginId = row.temp_login_id?.trim();
          if (!userId && !tempLoginId) invalid.push(i18n.t("upload_profile_visa.precheck_identity_required"));
          if (userId && tempLoginId) invalid.push(i18n.t("upload_profile_visa.precheck_temp_id_existing"));
          if (tempLoginId && !TEMP_LOGIN_ID_REGEX.test(tempLoginId)) invalid.push(i18n.t("upload_profile_visa.precheck_temp_id_invalid"));
          if (tempLoginId && (tempIdCounts.get(tempLoginId.toLowerCase()) ?? 0) > 1) invalid.push(i18n.t("upload_profile_visa.precheck_temp_id_duplicate"));

          // Date format check (YYYY/MM/DD) for non-empty date fields
          for (const dateField of DATE_FIELDS) {
            const val = row[dateField]?.trim();
            if (val && !DATE_REGEX.test(val)) {
              // Find human label from REQUIRED_FIELDS or fall back to field name
              const label = REQUIRED_FIELDS.find((f) => f.field === dateField)?.label ?? dateField;
              invalid.push(`${label} (invalid date format, expected YYYY/MM/DD)`);
            }
          }

          // Gender check
          const genderVal = row["gender"]?.trim().toUpperCase();
          if (genderVal && !VALID_GENDERS_CLIENT.has(genderVal)) {
            invalid.push(`Gender "${row["gender"]?.trim()}" is invalid (accepted: MALE, FEMALE)`);
          }

          // Country check
          const countryVal = row["country"]?.trim().toUpperCase();
          if (countryVal && !VALID_COUNTRIES_CLIENT.has(countryVal)) {
            invalid.push(`Country "${row["country"]?.trim()}" is not recognised`);
          }

          // Reference code checks (only when refs have loaded from API)
          if (refs) {
            const soVal = row["sending_org"]?.trim();
            if (soVal && !refs.sendingOrgs.includes(soVal)) {
              invalid.push(`Sending Organization "${soVal}" is not a recognised code`);
            }
            const ccVal = row["company_code"]?.trim();
            if (ccVal && !refs.companyCodes.includes(ccVal)) {
              invalid.push(`Company Code "${ccVal}" is not recognised`);
            }
            const vtVal = row["visa_type"]?.trim();
            if (vtVal && !refs.visaTypes.includes(vtVal)) {
              invalid.push(`Visa Type "${vtVal}" is not a recognised code`);
            }
          }

          // Field length checks
          for (const [field, { max, label }] of Object.entries(FIELD_LIMITS)) {
            const val = row[field]?.trim() ?? "";
            if (val.length > max) {
              invalid.push(`${label} exceeds ${max} characters (${val.length})`);
            }
          }

          // Email format check
          const emailVal = row["emergency_email"]?.trim();
          if (emailVal && !EMAIL_REGEX.test(emailVal)) {
            invalid.push(`Emergency Email "${emailVal}" is not a valid email address`);
          }

          if (missing.length > 0 || invalid.length > 0) {
            const name = [row.first_name, row.last_name].filter(Boolean).join(" ") || "—";
            issues.push({ row: i + 2, name, missing, invalid });
          }
        });
        resolve({
          total: rows.length,
          ready: rows.length - issues.length,
          createCount: rows.filter(isCreateRow).length,
          updateCount: rows.filter((row) => !!row.user_id?.trim() && !row.temp_login_id?.trim()).length,
          issues,
          preview: rows.slice(0, PREVIEW_LIMIT),
          headers: rawHeaders,
        });
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

// ── Role gate wrapper ─────────────────────────────────────────────────────────

export const UploadProfileVisaPage = () => {
  const { isFullAccess, roles } = usePermissions();
  if (!isFullAccess && !roles.includes("upload_user_data_write")) {
    return <Navigate to="/other-setup" replace />;
  }
  return <UploadProfileVisaContent />;
};

// ── Main content ──────────────────────────────────────────────────────────────

const UploadProfileVisaContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  // ── Export filter state ────────────────────────────────────────────────────
  const [countryFilter, setCountryFilter]     = useState<string[]>([]);
  const [companyFilter, setCompanyFilter]     = useState<string[]>([]);
  const [sendingOrgFilter, setSendingOrgFilter] = useState("");
  const [batchFilter, setBatchFilter]         = useState("");

  // ── Dropdown data ──────────────────────────────────────────────────────────
  const [companies, setCompanies]   = useState<CompanyOption[]>([]);
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);

  // ── Country multi-select state ─────────────────────────────────────────────
  const [countrySearch, setCountrySearch]           = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // ── Company multi-select state ─────────────────────────────────────────────
  const [companySearch, setCompanySearch]           = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // ── Export action state ────────────────────────────────────────────────────
  const [exporting, setExporting]     = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // ── Import state ───────────────────────────────────────────────────────────
  const fileInputRef                        = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile]     = useState<File | null>(null);
  const [preCheck, setPreCheck]             = useState<PreCheckResult | null>(null);
  const [preChecking, setPreChecking]       = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [importError, setImportError]       = useState<string | null>(null);
  const [result, setResult]                 = useState<ImportResult | null>(null);
  const [activeTab, setActiveTab]           = useState<ResultTab>("succeeded");
  const [preCheckTab, setPreCheckTab]       = useState<"issues" | "ready">("issues");
  const [pollingLogId, setPollingLogId]     = useState<number | null>(null);

  // ── Reference codes for pre-check ─────────────────────────────────────────
  const [refs, setRefs] = useState<ReferenceCodes | null>(null);

  // ── Upload history ─────────────────────────────────────────────────────────
  const [history, setHistory]             = useState<UploadLogEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [logDetail, setLogDetail]         = useState<Record<number, UploadLogRow[]>>({});
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  // ── Load company dropdown options ─────────────────────────────────────────
  const refreshDropdowns = () => {
    companyService.getDropdown().then((data: any[]) => {
      setCompanies(
        data.map((c: any) => ({ value: c.value ?? c.id ?? "", label: c.label ?? c.company_name ?? "" }))
      );
    }).catch(console.error);
  };
  useEffect(() => { refreshDropdowns(); }, []); // eslint-disable-line

  // ── Load sending orgs when exactly 1 country is selected ──────────────────
  useEffect(() => {
    if (countryFilter.length === 1) {
      sendingOrgService.getDropdown(countryFilter[0])
        .then((data: any[]) =>
          setSendingOrgs(data.map((o: any) => ({ value: o.value ?? o.id, label: o.label ?? o.name })))
        )
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
      setSendingOrgFilter("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(countryFilter)]);

  // ── Outside-click handlers ─────────────────────────────────────────────────
  useEffect(() => {
    if (!countryDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [countryDropdownOpen]);

  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [companyDropdownOpen]);

  // ── Fetch reference codes for client-side pre-check ───────────────────────
  useEffect(() => {
    bulkUserService.getReferenceCodes()
      .then(setRefs)
      .catch(console.error);
  }, []);

  // ── Re-run pre-check when refs load (if a file is already selected) ────────
  useEffect(() => {
    if (!selectedFile || !refs) return;
    setPreChecking(true);
    runPreCheck(selectedFile, refs)
      .then((pc) => { setPreCheck(pc); setPreCheckTab(pc.issues.length > 0 ? "issues" : "ready"); })
      .catch(() => setPreCheck(null))
      .finally(() => setPreChecking(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs]);

  // ── Poll history until the background job completes ────────────────────────
  useEffect(() => {
    if (!pollingLogId) return;
    const interval = setInterval(async () => {
      try {
        const logs = await bulkUserService.getUploadHistory();
        const log  = logs.find((l) => l.id === pollingLogId);
        if (!log || log.status !== "processing") {
          clearInterval(interval);
          setPollingLogId(null);
          setHistory(logs);
          if (log) {
            // Fetch per-row detail and reconstruct ImportResult for inline display
            const rows = await bulkUserService.getUploadHistoryDetail(log.id);
            setLogDetail((prev) => ({ ...prev, [log.id]: rows }));
            const result: ImportResult = {
              logId: log.id,
              succeeded: rows.filter((r) => r.status === "success").map((r) => ({
                row: r.row_number, user_id: r.user_id ?? "", name: r.full_name ?? "", action: r.action ?? undefined, temp_login_id: r.temp_login_id ?? undefined, temp_password: r.temp_password ?? undefined,
              })),
              failed: rows.filter((r) => r.status === "error").map((r) => ({
                row: r.row_number, user_id: r.user_id ?? "", name: r.full_name ?? "", reason: r.error_detail ?? "",
              })),
            };
            setResult(result);
            setActiveTab(result.failed.length > 0 ? "failed" : "succeeded");
          }
        }
      } catch { /* network blip — keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingLogId]);

  // ── Auto-resume polling if a processing job is found on mount ──────────────
  useEffect(() => {
    if (pollingLogId) return; // already polling
    const inProgress = history.find((l) => l.status === "processing");
    if (inProgress) setPollingLogId(inProgress.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // ── Fetch upload history on mount ──────────────────────────────────────────
  const fetchHistory = () => {
    setHistoryLoading(true);
    bulkUserService.getUploadHistory()
      .then(setHistory)
      .catch(console.error)
      .finally(() => setHistoryLoading(false));
  };
  useEffect(() => { fetchHistory(); }, []); // eslint-disable-line

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const activeFilters: ExportFilters = {
        ...(countryFilter.length > 0   && { country: countryFilter }),
        ...(sendingOrgFilter           && { sending_org: sendingOrgFilter }),
        ...(companyFilter.length > 0   && { company: companyFilter }),
        ...(batchFilter.trim()         && { batch_no: batchFilter.trim() }),
      };
      await bulkUserService.exportCsv(activeFilters);
    } catch {
      setExportError(t("upload_profile_visa.export_error"));
    } finally {
      setExporting(false);
    }
  };

  const copyValue = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedValue(value);
    window.setTimeout(() => setCopiedValue(null), 2_000);
  };

  const resetPassword = async (logId: number, row: UploadLogRow) => {
    if (!row.user_id || !window.confirm(t("upload_profile_visa.reset_password_confirm"))) return;
    setResettingUserId(row.user_id);
    try {
      const { password } = await bulkUserService.resetTempPassword(row.user_id);
      setLogDetail((current) => ({
        ...current,
        [logId]: current[logId].map((item) => item.row_number === row.row_number ? { ...item, temp_password: password } : item),
      }));
    } finally {
      setResettingUserId(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
    setImportError(null);
    setPreCheck(null);
    if (file) {
      setPreChecking(true);
      setPreCheckTab("issues");
      try {
        const pc = await runPreCheck(file, refs);
        setPreCheck(pc);
        setPreCheckTab(pc.issues.length > 0 ? "issues" : "ready");
      } catch {
        setPreCheck(null);
      } finally {
        setPreChecking(false);
      }
    }
  };

  const handleBrowseClick = () => {
    setSelectedFile(null);
    setPreCheck(null);
    setResult(null);
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setImportError(null);
    setResult(null);
    try {
      const { logId } = await bulkUserService.importCsv(selectedFile);
      setPollingLogId(logId);

      // ── Reset Steps 1 & 2 immediately after submission ──────────────────
      // Step 2: clear the attached file and pre-check panel
      setSelectedFile(null);
      setPreCheck(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Step 1: refresh company dropdown in case data changed
      refreshDropdowns();
      fetchHistory(); // show "processing" row immediately
    } catch {
      setImportError(t("upload_profile_visa.import_error"));
    } finally {
      setUploading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl">
      {/* Page header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/other-setup")}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">{t("upload_profile_visa.title")}</h1>
            <p className="text-xs text-gray-400">{t("upload_profile_visa.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">

        {/* ── Section 1: Export ──────────────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-5">
            <StepBadge n={1} />
            <div>
              <h2 className="font-semibold text-gray-900">{t("upload_profile_visa.export_section_title")}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{t("upload_profile_visa.export_section_desc")}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">

            {/* Country multi-select */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("upload_profile_visa.filter_country")}
              </label>
              {countryFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {countryFilter.map((code) => {
                    const opt = COUNTRY_OPTIONS.find((c) => c.code === code);
                    return (
                      <span key={code} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        {opt ? t(`profile.${opt.key}`) : code}
                        <button type="button" onClick={() => setCountryFilter((prev) => prev.filter((c) => c !== code))} className="hover:text-indigo-900">&times;</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative" ref={countryDropdownRef}>
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => { setCountrySearch(e.target.value); if (!countryDropdownOpen) setCountryDropdownOpen(true); }}
                  onFocus={() => setCountryDropdownOpen(true)}
                  placeholder={t("upload_profile_visa.filter_country_placeholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {countryDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {COUNTRY_OPTIONS.filter((opt) =>
                      t(`profile.${opt.key}`).toLowerCase().includes(countrySearch.toLowerCase())
                    ).map((opt) => {
                      const isSelected = countryFilter.includes(opt.code);
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          onClick={() => {
                            setCountryFilter((prev) =>
                              prev.includes(opt.code) ? prev.filter((c) => c !== opt.code) : [...prev, opt.code]
                            );
                            setCountrySearch("");
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                            {isSelected && "\u2713"}
                          </span>
                          {t(`profile.${opt.key}`)}
                        </button>
                      );
                    })}
                    {COUNTRY_OPTIONS.filter((opt) =>
                      t(`profile.${opt.key}`).toLowerCase().includes(countrySearch.toLowerCase())
                    ).length === 0 && (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sending Org select */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("upload_profile_visa.filter_sending_org")}
              </label>
              <select
                value={sendingOrgFilter}
                onChange={(e) => setSendingOrgFilter(e.target.value)}
                disabled={sendingOrgs.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <option value="">
                  {sendingOrgs.length === 0
                    ? t("upload_profile_visa.filter_sending_org_hint")
                    : t("upload_profile_visa.filter_all")}
                </option>
                {sendingOrgs.map((org) => (
                  <option key={org.value} value={org.value}>{org.label}</option>
                ))}
              </select>
            </div>

            {/* Company multi-select */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("upload_profile_visa.filter_company")}
              </label>
              {companyFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {companyFilter.map((val) => {
                    const match = companies.find((c) => c.value === val);
                    return (
                      <span key={val} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        {match?.label || val}
                        <button type="button" onClick={() => setCompanyFilter((prev) => prev.filter((c) => c !== val))} className="hover:text-indigo-900">&times;</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative" ref={companyDropdownRef}>
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => { setCompanySearch(e.target.value); if (!companyDropdownOpen) setCompanyDropdownOpen(true); }}
                  onFocus={() => setCompanyDropdownOpen(true)}
                  placeholder={t("upload_profile_visa.filter_company_placeholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {companyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {companies.filter((c) =>
                      c.label?.toLowerCase().includes(companySearch.toLowerCase())
                    ).map((c) => {
                      const isSelected = companyFilter.includes(c.value);
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => {
                            setCompanyFilter((prev) =>
                              prev.includes(c.value) ? prev.filter((v) => v !== c.value) : [...prev, c.value]
                            );
                            setCompanySearch("");
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                            {isSelected && "\u2713"}
                          </span>
                          {c.label}
                        </button>
                      );
                    })}
                    {companies.filter((c) =>
                      c.label?.toLowerCase().includes(companySearch.toLowerCase())
                    ).length === 0 && (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Batch No text input */}
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("upload_profile_visa.filter_batch_no")}
              </label>
              <input
                type="text"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                placeholder={t("upload_profile_visa.filter_all")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {exportError && <p className="text-sm text-red-600 mb-3">{exportError}</p>}

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? (
                <><Spinner />{t("upload_profile_visa.exporting")}</>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t("upload_profile_visa.export_btn")}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => void bulkUserService.downloadTemplate("new")}
              className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 bg-white text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {t("upload_profile_visa.download_new_user_template")}
            </button>
          </div>
        </div>

        {/* ── Section 2: Select & Pre-check ─────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-5">
            <StepBadge n={2} />
            <div>
              <h2 className="font-semibold text-gray-900">{t("upload_profile_visa.import_section_title")}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{t("upload_profile_visa.import_section_desc")}</p>
            </div>
          </div>

          {/* Which file am I uploading? */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-sm font-semibold text-gray-800">{t("upload_profile_visa.mode_update_title")}</p>
              <p className="text-xs text-gray-500 mt-1">{t("upload_profile_visa.mode_update_desc")}</p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
              <p className="text-sm font-semibold text-indigo-800">{t("upload_profile_visa.mode_create_title")}</p>
              <p className="text-xs text-indigo-700 mt-1">{t("upload_profile_visa.mode_create_desc")}</p>
            </div>
          </div>

          {/* File drop zone */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <div
            onClick={handleBrowseClick}
            className="cursor-pointer border-2 border-dashed border-gray-300 rounded-lg px-6 py-8 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors mb-4"
          >
            <svg className="mx-auto w-10 h-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z" />
            </svg>
            {selectedFile ? (
              <p className="text-sm font-medium text-indigo-700">{selectedFile.name}</p>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700">{t("upload_profile_visa.select_file")}</p>
                <p className="text-xs text-gray-400 mt-1">{t("upload_profile_visa.file_hint")}</p>
              </>
            )}
          </div>
          {/* Pre-check results */}
          {preChecking && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner />{t("upload_profile_visa.prechecking")}
            </div>
          )}

          {preCheck && !preChecking && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Summary bar */}
              <div className={`px-4 py-2.5 text-sm font-medium flex items-center justify-between ${
                preCheck.issues.length === 0
                  ? "bg-emerald-50 text-emerald-700"
                  : preCheck.ready === 0
                    ? "bg-red-50 text-red-700"
                    : "bg-amber-50 text-amber-700"
              }`}>
                <span>
                  {preCheck.issues.length === 0
                    ? t("upload_profile_visa.precheck_all_ok", { count: preCheck.total })
                    : preCheck.ready === 0
                      ? t("upload_profile_visa.precheck_all_fail", { count: preCheck.total })
                      : t("upload_profile_visa.precheck_partial", { ready: preCheck.ready, issues: preCheck.issues.length })
                  }
                </span>
                <span className="text-xs font-normal opacity-70">{t("upload_profile_visa.precheck_note")}</span>
              </div>

              {/* Tabs */}
              {preCheck.issues.length > 0 && (
                <>
                  <div className="flex border-b border-gray-100 bg-white">
                    <button
                      onClick={() => setPreCheckTab("issues")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${preCheckTab === "issues" ? "text-red-700 border-b-2 border-red-500 bg-red-50" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {t("upload_profile_visa.precheck_tab_issues", { count: preCheck.issues.length })}
                    </button>
                    <button
                      onClick={() => setPreCheckTab("ready")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${preCheckTab === "ready" ? "text-emerald-700 border-b-2 border-emerald-500 bg-emerald-50" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {t("upload_profile_visa.precheck_tab_ready", { count: preCheck.ready })}
                      <span className="ml-2 opacity-75">
                        {t("upload_profile_visa.precheck_create_count", { count: preCheck.createCount })} · {t("upload_profile_visa.precheck_update_count", { count: preCheck.updateCount })}
                      </span>
                    </button>
                  </div>

                  {preCheckTab === "issues" && (
                    <div className="max-h-48 overflow-y-auto bg-white">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                            <th className="px-4 py-2 w-12">{t("upload_profile_visa.col_row")}</th>
                            <th className="px-4 py-2">{t("upload_profile_visa.col_name")}</th>
                            <th className="px-4 py-2">{t("upload_profile_visa.precheck_missing_fields")}</th>
                            <th className="px-4 py-2">{t("upload_profile_visa.precheck_invalid_fields")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {preCheck.issues.map((issue) => (
                            <tr key={issue.row}>
                              <td className="px-4 py-1.5 text-gray-400 tabular-nums">{issue.row}</td>
                              <td className="px-4 py-1.5 text-gray-700">{issue.name}</td>
                              <td className="px-4 py-1.5 text-red-600">{issue.missing.join(", ") || "—"}</td>
                              <td className="px-4 py-1.5 text-orange-600">{issue.invalid.join(", ") || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Data preview */}
          {preCheck && !preChecking && preCheck.preview.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("upload_profile_visa.preview_title")}
                </p>
                <p className="text-xs text-gray-400 italic">
                  {t("upload_profile_visa.preview_note", { limit: PREVIEW_LIMIT })}
                </p>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="text-xs whitespace-nowrap">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {preCheck.headers.map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[10px]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {preCheck.preview.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {preCheck.headers.map((h) => {
                          const field = HEADER_TO_FIELD[h] ?? h;
                          const val = (row[field] ?? "").trim();
                          const isMissing = requiredFieldsFor(row).some((f) => f.field === field) && !val;
                          const isInvalidDate = DATE_FIELDS.has(field) && val && !DATE_REGEX.test(val);
                          const isInvalidGender = field === "gender" && val && !VALID_GENDERS_CLIENT.has(val.toUpperCase());
                          const isInvalidCountry = field === "country" && val && !VALID_COUNTRIES_CLIENT.has(val.toUpperCase());
                          const isBadSendingOrg = field === "sending_org" && val && refs && !refs.sendingOrgs.includes(val);
                          const isBadCompanyCode = field === "company_code" && val && refs && !refs.companyCodes.includes(val);
                          const isBadVisaType = field === "visa_type" && val && refs && !refs.visaTypes.includes(val);
                          const isTooLong = field in FIELD_LIMITS && val.length > FIELD_LIMITS[field].max;
                          const isBadEmail = field === "emergency_email" && val && !EMAIL_REGEX.test(val);
                          const isInvalid = isInvalidDate || isInvalidGender || isInvalidCountry || isBadSendingOrg || isBadCompanyCode || isBadVisaType || isTooLong || isBadEmail;
                          return (
                            <td key={h} className={`px-3 py-1.5 ${isMissing ? "text-red-500 font-medium" : isInvalid ? "text-orange-500 font-medium" : "text-gray-700"}`}>
                              {val || (isMissing ? "—" : "")}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 3: Load to System ──────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-5">
            <StepBadge n={3} />
            <div>
              <h2 className="font-semibold text-gray-900">{t("upload_profile_visa.load_section_title")}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{t("upload_profile_visa.load_section_desc")}</p>
            </div>
          </div>

          {pollingLogId && !result && (
            <div className="flex items-center gap-2 mb-3 text-sm text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2.5">
              <Spinner />
              {t("upload_profile_visa.uploading_background")}
            </div>
          )}
          {importError && <p className="text-sm text-red-600 mb-3">{importError}</p>}

          <button
            onClick={handleImport}
            disabled={!selectedFile || uploading || !!pollingLogId || preChecking || (preCheck !== null && preCheck.issues.length > 0)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <><Spinner />{t("upload_profile_visa.uploading")}</>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {t("upload_profile_visa.import_btn")}
              </>
            )}
          </button>
        </div>

        {/* ── Section 4: Results ─────────────────────────────────────────── */}
        {result && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            {/* Summary banner */}
            <div className={`px-6 py-3 text-sm font-medium border-b ${
              result.failed.length === 0
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : result.succeeded.length === 0
                  ? "bg-red-50 text-red-700 border-red-100"
                  : "bg-amber-50 text-amber-700 border-amber-100"
            }`}>
              {result.failed.length === 0
                ? t("upload_profile_visa.summary_all_ok",    { count: result.succeeded.length })
                : result.succeeded.length === 0
                  ? t("upload_profile_visa.summary_all_fail", { count: result.failed.length })
                  : t("upload_profile_visa.summary_partial",  { success: result.succeeded.length, failed: result.failed.length })
              }
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100">
              <TabButton
                label={t("upload_profile_visa.tab_succeeded", { count: result.succeeded.length })}
                active={activeTab === "succeeded"}
                variant="success"
                onClick={() => setActiveTab("succeeded")}
              />
              <TabButton
                label={t("upload_profile_visa.tab_failed", { count: result.failed.length })}
                active={activeTab === "failed"}
                variant={result.failed.length > 0 ? "danger" : "success"}
                onClick={() => setActiveTab("failed")}
              />
            </div>

            {/* Tab content */}
            <div className="px-6 py-4">
              {activeTab === "succeeded" && (
                result.succeeded.length === 0
                  ? <p className="text-sm text-gray-500">{t("upload_profile_visa.empty_succeeded")}</p>
                  : (
                    <div>
                      {result.logId !== undefined && result.succeeded.some((r) => r.action === "created") && (
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => void bulkUserService.exportUploadResults(result.logId!)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            {t("upload_profile_visa.download_results")}
                          </button>
                          <p className="text-xs text-gray-500">{t("upload_profile_visa.temp_password_hint")}</p>
                        </div>
                      )}
                      <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                            <th className="pb-2 pr-4 w-12">{t("upload_profile_visa.col_row")}</th>
                            <th className="pb-2 pr-4">{t("upload_profile_visa.col_name")}</th>
                            <th className="pb-2 pr-4">{t("upload_profile_visa.action")}</th>
                            <th className="pb-2 pr-4">{t("upload_profile_visa.temp_login_id")}</th>
                            <th className="pb-2">{t("upload_profile_visa.temp_password")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {result.succeeded.map((r) => (
                            <tr key={r.row}>
                              <td className="py-2 pr-4 text-gray-400 tabular-nums">{r.row}</td>
                              <td className="py-2 pr-4 text-gray-700">
                                <span className="inline-flex items-center gap-1.5">
                                  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                  {r.name}
                                </span>
                              </td>
                              <td className="py-2 pr-4">{r.action ? <Badge variant={r.action === "created" ? "success" : "info"}>{t(`upload_profile_visa.action_${r.action}`)}</Badge> : "—"}</td>
                              <td className="py-2 pr-4 font-mono text-gray-700">{r.action === "created" ? (r.temp_login_id ?? "—") : "—"}</td>
                              <td className="py-2">
                                {r.action !== "created" ? "—" : r.temp_password ? (
                                  <span className="inline-flex items-center gap-2">
                                    <code className="font-mono text-gray-800">{r.temp_password}</code>
                                    <button type="button" className="text-xs text-indigo-600" onClick={() => void copyValue(r.temp_password!)}>{t(copiedValue === r.temp_password ? "upload_profile_visa.copied" : "upload_profile_visa.copy")}</button>
                                  </span>
                                ) : <span className="text-gray-400">{t("upload_profile_visa.temp_password_expired")}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )
              )}

              {activeTab === "failed" && (
                result.failed.length === 0
                  ? <p className="text-sm text-gray-500">{t("upload_profile_visa.empty_failed")}</p>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                            <th className="pb-2 pr-4 w-12">{t("upload_profile_visa.col_row")}</th>
                            <th className="pb-2 pr-4">{t("upload_profile_visa.col_name")}</th>
                            <th className="pb-2">{t("upload_profile_visa.col_reason")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {result.failed.map((r) => (
                            <tr key={r.row}>
                              <td className="py-2 pr-4 text-gray-400 tabular-nums">{r.row}</td>
                              <td className="py-2 pr-4 text-gray-700">{r.name}</td>
                              <td className="py-2 text-red-600">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
              )}
            </div>
          </div>
        )}

        {/* ── Section 5: Upload History ──────────────────────────────────── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 text-sm">{t("upload_profile_visa.history_title")}</h2>
            <button
              onClick={fetchHistory}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {t("upload_profile_visa.history_refresh")}
            </button>
          </div>

          {historyLoading ? (
            <div className="px-6 py-8 flex justify-center"><Spinner /></div>
          ) : history.length === 0 ? (
            <p className="px-6 py-6 text-sm text-gray-400">{t("upload_profile_visa.history_empty")}</p>
          ) : (
            <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
              {history.map((log) => (
                <div key={log.id}>
                  {/* Log row */}
                  <button
                    className="w-full text-left px-6 py-3 hover:bg-gray-50 transition-colors"
                    onClick={async () => {
                      if (expandedLogId === log.id) {
                        setExpandedLogId(null);
                        return;
                      }
                      setExpandedLogId(log.id);
                      if (!logDetail[log.id]) {
                        setLoadingDetailId(log.id);
                        try {
                          const rows = await bulkUserService.getUploadHistoryDetail(log.id);
                          setLogDetail((prev) => ({ ...prev, [log.id]: rows }));
                        } catch { /* ignore */ } finally {
                          setLoadingDetailId(null);
                        }
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 text-sm">
                      {/* ID */}
                      <span className="text-[10px] font-mono text-gray-400 flex-shrink-0 w-10 text-right">
                        #{log.id}
                      </span>
                      {/* Status badge */}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 ${
                        log.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                        log.status === "failed"    ? "bg-red-100 text-red-700" :
                                                     "bg-amber-100 text-amber-700"
                      }`}>
                        {log.status}
                      </span>
                      {/* File name */}
                      <span className="text-gray-700 font-medium truncate max-w-[200px]">
                        {log.file_name ?? "—"}
                      </span>
                      {/* Counts */}
                      <span className="text-gray-400 text-xs ml-auto flex-shrink-0">
                        <span className="text-emerald-600 font-medium">{log.success_count}</span>
                        {" / "}
                        <span className={log.error_count > 0 ? "text-red-600 font-medium" : "text-gray-400"}>{log.error_count} err</span>
                        {" · "}
                        {log.total_rows} rows
                      </span>
                      {/* Uploader + date */}
                      <span className="text-gray-400 text-xs flex-shrink-0">
                        {log.uploaded_by_name ?? ""}
                        {" · "}
                        {new Date(log.uploaded_at).toLocaleString()}
                      </span>
                      {/* Chevron */}
                      <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${expandedLogId === log.id ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expandable detail */}
                  {expandedLogId === log.id && (
                    <div className="px-6 pb-4 bg-gray-50 border-t border-gray-100">
                      {loadingDetailId === log.id ? (
                        <div className="py-4 flex justify-center"><Spinner /></div>
                      ) : !logDetail[log.id] ? (
                        <p className="py-3 text-sm text-gray-400">{t("upload_profile_visa.history_detail_error")}</p>
                      ) : (
                        <div className="mt-3">
                          {logDetail[log.id].some((row) => row.action === "created") && (
                            <button type="button" onClick={() => void bulkUserService.exportUploadResults(log.id)} className="mb-3 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                              {t("upload_profile_visa.download_results")}
                            </button>
                          )}
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                          <table className="w-full text-xs">
                            <thead className="bg-white border-b border-gray-100">
                              <tr className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                <th className="px-4 py-2 w-12">{t("upload_profile_visa.col_row")}</th>
                                <th className="px-4 py-2">{t("upload_profile_visa.col_name")}</th>
                                <th className="px-4 py-2">{t("upload_profile_visa.action")}</th>
                                <th className="px-4 py-2">{t("upload_profile_visa.temp_login_id")}</th>
                                <th className="px-4 py-2">{t("upload_profile_visa.temp_password")}</th>
                                <th className="px-4 py-2 w-16">{t("upload_profile_visa.history_col_status")}</th>
                                <th className="px-4 py-2">{t("upload_profile_visa.col_reason")}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 bg-white">
                              {logDetail[log.id].map((r) => (
                                <tr key={r.row_number}>
                                  <td className="px-4 py-1.5 text-gray-400 tabular-nums">{r.row_number}</td>
                                  <td className="px-4 py-1.5 text-gray-700">{r.full_name ?? "—"}</td>
                                  <td className="px-4 py-1.5">{r.action ? <Badge variant={r.action === "created" ? "success" : "info"}>{t(`upload_profile_visa.action_${r.action}`)}</Badge> : "—"}</td>
                                  <td className="px-4 py-1.5 font-mono text-gray-700">{r.action === "created" ? (r.temp_login_id ?? "—") : (r.user_id ?? "—")}</td>
                                  <td className="px-4 py-1.5">
                                    {r.action !== "created" ? "—" : r.temp_password ? (
                                      <span className="inline-flex items-center gap-2">
                                        <code>{r.temp_password}</code>
                                        <button type="button" className="text-indigo-600" onClick={() => void copyValue(r.temp_password!)}>{t(copiedValue === r.temp_password ? "upload_profile_visa.copied" : "upload_profile_visa.copy")}</button>
                                      </span>
                                    ) : <span className="text-gray-400">{t("upload_profile_visa.temp_password_expired")}</span>}
                                    {r.action === "created" && canWrite("profile") && r.user_id && (
                                      <button type="button" disabled={resettingUserId === r.user_id} onClick={() => void resetPassword(log.id, r)} className="ml-2 text-indigo-600 disabled:text-gray-400">
                                        {t("upload_profile_visa.reset_password")}
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-4 py-1.5">
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase ${r.status === "success" ? "text-emerald-600" : "text-red-600"}`}>
                                      {r.status === "success"
                                        ? <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                        : <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                                      }
                                      {r.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-1.5 text-gray-500">{r.error_detail ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// ── Small reusable sub-components ─────────────────────────────────────────────

const StepBadge = ({ n }: { n: number }) => (
  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
    <span className="text-indigo-700 font-bold text-sm">{n}</span>
  </div>
);

const Spinner = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
  </svg>
);

const TabButton = ({
  label, active, variant, onClick,
}: {
  label: string; active: boolean; variant: "success" | "danger"; onClick: () => void;
}) => {
  const activeClass = variant === "danger"
    ? "text-red-700 border-b-2 border-red-500 bg-red-50"
    : "text-indigo-700 border-b-2 border-indigo-600 bg-indigo-50";
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
        active ? activeClass : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );
};
